import type { components } from "@cntrl/api-client";
import { useFanClubOverview } from "../../app/hooks/useAnnouncements.js";
import styles from "./HubMetrics.module.scss";

type FanClubOverview = components["schemas"]["FanClubOverview"];

const DEGRADED = "—";

/** Whole-dollar USD (no cents) — matches the shot-37 revenue card. */
function usd(cents: number | null | undefined): string {
  return ((cents ?? 0) / 100).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/** A 0–1 rate as a whole-percent, or the em-dash when the server has no rate yet (reach not snapshotted).*/
function pct(rate: number | null | undefined): string {
  return rate == null ? DEGRADED : `${Math.round(rate * 100)}%`;
}

interface Kpi {
  key: string;
  label: string;
  value: (o: FanClubOverview) => string;
  caption: string;
  accent?: boolean;
  /** Absent ⇒ always shown. Present ⇒ the card is DROPPED when it returns false (a gated figure). */
  shown?: (o: FanClubOverview) => boolean;
}

// The shot-37 KPI cards. FOUR of the five are audience and ops; MRR is money.
//
// ⚠️ That last part reversed on 2026-09-03, and this comment used to argue the other way ("MRR rides
// antenna:read ... it is the band's OWN club performance, so no separate money-capability gate"). Fan-club
// recurring revenue is one of the five money CATEGORIES, and the seats reading this hub are not only the
// band: a management org holds antenna:manage and a label holds antenna:read, so "the band's own club" was
// never the whole audience. The server sends `revenue` as a nullable BLOCK — null for a seat without
// money:antenna — and the card is DROPPED rather than dashed, because the em-dash means "we hold no figure"
// and this seat's absence is "not yours to see". The other four render unchanged for everyone.
const KPIS: Kpi[] = [
  { key: "fans", label: "Total fans", value: (o) => String(o.fans), caption: "Following you on Antenna" },
  { key: "paying", label: "Paying members", value: (o) => String(o.payingMembers), caption: "Across your paid tiers" },
  { key: "mrr", label: "Monthly revenue", value: (o) => usd(o.revenue?.mrrCents), caption: "Recurring · comps excluded",
    shown: (o) => o.revenue != null },
  { key: "seen", label: "Avg. seen rate", value: (o) => pct(o.avgSeenRate), caption: "of fans open what you post", accent: true },
  { key: "backstage", label: "Backstage items", value: (o) => String(o.backstageCount), caption: "Members-only content" },
];

/**
 * The shot-37 hub metrics: the 5-KPI strip + the Membership tier breakdown, sourced from the Antenna
 * band-metrics API via `GET /antenna/overview`. Every figure is the band's OWN club. When the
 * API is unavailable the overview degrades to zeros / null seen-rate / no tiers and this renders its
 * born-blank state — never a fabricated number, never an error banner (a KPI strip must not block the hub).
 */
export function HubMetrics() {
  const overview = useFanClubOverview();
  const o = overview.data;
  const ready = !overview.isLoading && !overview.isError && o != null;

  return (
    <div className={styles.metrics}>
      <section className={styles.kpis} aria-label="Fan club KPIs">
        {KPIS.filter((kpi) => !ready || !kpi.shown || kpi.shown(o)).map((kpi) => (
          <div key={kpi.key} className={styles.card} data-accent={kpi.accent ? "" : undefined}>
            <span className={styles.cardLabel}>{kpi.label}</span>
            <span className={styles.cardValue}>{ready ? kpi.value(o) : DEGRADED}</span>
            <span className={styles.cardCaption}>{kpi.caption}</span>
          </div>
        ))}
      </section>

      {ready && (o.tiers?.length ?? 0) > 0 && (
        <MembershipBreakdown tiers={o.tiers ?? []} mrrCents={o.revenue?.mrrCents} />
      )}
    </div>
  );
}

type Tier = NonNullable<FanClubOverview["tiers"]>[number];

function MembershipBreakdown({ tiers, mrrCents }: { tiers: Tier[]; mrrCents: number | undefined }) {
  const max = Math.max(1, ...tiers.map((t) => t.members ?? 0));
  return (
    <section className={styles.membership} aria-label="Membership by tier">
      <p className={styles.sectionLabel}>Membership</p>
      <ul className={styles.tierRows}>
        {tiers.map((t, i) => {
          const members = t.members ?? 0;
          const price = t.priceCents ?? 0;
          return (
            <li key={t.tierId ?? String(i)} className={styles.tierRow}>
              <span className={styles.tierName}>{t.name ?? "Tier"}</span>
              <span className={styles.tierPrice}>{price > 0 ? `${usd(price)}/mo` : "Free"}</span>
              <span className={styles.tierBar} aria-hidden="true">
                <span className={styles.tierFill} style={{ width: `${(members / max) * 100}%` }} />
              </span>
              <span className={styles.tierCount}>{members}</span>
            </li>
          );
        })}
      </ul>
      {/* The tier ROWS are membership counts and list prices — a tier's price is what a fan is charged, so it
          is public, not gated. Only this footer totals what the club EARNS, so only it follows the block. */}
      {mrrCents != null && (
        <div className={styles.tierFooter}>
          <span>Monthly recurring</span>
          <span className={styles.tierFooterValue}>{usd(mrrCents)}</span>
        </div>
      )}
    </section>
  );
}
